/**
 * The age a weight is read at on the WHO growth curves, and why a weight gets
 * no percentile. Like `age.ts`, every date is a calendar date (YYYY-MM-DD),
 * never an instant: the weight's date is its day in the caller's zone (the
 * phone's, or the household's for the assistant).
 *
 * US pediatricians use the WHO Child Growth Standards from birth to age 2,
 * then the CDC 2000 charts, so Babia reads a weight on the WHO curves only up
 * to 730 days. A baby born more than three weeks before the due date (before
 * 37 weeks) is read at corrected age, counted from the due date, as the
 * clinic plots it. Before the due date the WHO curves have no row, so such a
 * weight gets no percentile. A baby with no due date counts as born on time.
 */
import { DAY_MS } from "./calendar.ts";
import { earliestBirthDate, utcDay } from "./age.ts";

export const SEXES = ["female", "male"] as const;
/** Used only to pick the WHO curves, which differ for girls and boys. */
export type Sex = (typeof SEXES)[number];

/** The sex as a choice to pick in Settings. */
export const SEX_LABEL: Record<Sex, string> = { female: "Girl", male: "Boy" };

/** Born more than this many days before the due date: read at corrected age. */
export const PRETERM_DAYS = 21;
/** The last day of age the WHO curves are read at: age 2. */
export const GROWTH_MAX_DAYS = 730;
/** The earliest due date: 4 weeks before the birth date (born at 44 weeks). */
export const DUE_BEFORE_BIRTH_DAYS = 28;
/** The latest due date: 18 weeks after the birth date (born at 22 weeks). */
export const DUE_AFTER_BIRTH_DAYS = 126;

/** Whole days from `from` to `to`: negative when `to` comes first. */
export function daysBetween(from: string, to: string): number {
  return Math.round((utcDay(to) - utcDay(from)) / DAY_MS);
}

/** The date `days` after `date` (before it when negative). */
export function addDays(date: string, days: number): string {
  return new Date(utcDay(date) + days * DAY_MS).toISOString().slice(0, 10);
}

/** Whether a due date belongs with a birth date: from 4 weeks before it to 18 weeks after it. */
export function dueDateFits(birthDate: string, dueDate: string): boolean {
  const early = daysBetween(birthDate, dueDate);
  return early >= -DUE_BEFORE_BIRTH_DAYS && early <= DUE_AFTER_BIRTH_DAYS;
}

/** Born more than three weeks before the due date, so read at corrected age. */
export function isPreterm(birthDate: string, dueDate: string | null): boolean {
  if (dueDate === null || !dueDateFits(birthDate, dueDate)) return false;
  return daysBetween(birthDate, dueDate) > PRETERM_DAYS;
}

/**
 * The due dates Settings offers on `today`: around the birth date when there
 * is one, or else from the earliest birth date Babia takes to 18 weeks ahead.
 */
export function dueDateRange(
  birthDate: string | null,
  today: string,
): { min: string; max: string } {
  if (birthDate !== null) {
    return {
      min: addDays(birthDate, -DUE_BEFORE_BIRTH_DAYS),
      max: addDays(birthDate, DUE_AFTER_BIRTH_DAYS),
    };
  }
  return { min: earliestBirthDate(today), max: addDays(today, DUE_AFTER_BIRTH_DAYS) };
}

/** What the WHO curves need to know about a baby. */
export interface GrowthBaby {
  sex: Sex | null;
  birthDate: string | null;
  dueDate: string | null;
}

/** Why a weight gets no percentile. */
export type NoGrowthReason =
  | "no-weight"
  | "no-sex"
  | "no-birth-date"
  | "before-birth"
  | "before-due-date"
  | "past-two";

/** The day of age a weight is read at, or why it is not read. */
export type GrowthAge = { sex: Sex; days: number; corrected: boolean } | { reason: NoGrowthReason };

/** The age, in days, at which `baby`'s weight on `date` is read on the WHO curves. */
export function growthAge(baby: GrowthBaby, date: string): GrowthAge {
  if (baby.sex === null) return { reason: "no-sex" };
  if (baby.birthDate === null) return { reason: "no-birth-date" };
  const age = daysBetween(baby.birthDate, date);
  if (age < 0) return { reason: "before-birth" };
  if (age > GROWTH_MAX_DAYS) return { reason: "past-two" };
  if (isPreterm(baby.birthDate, baby.dueDate)) {
    const corrected = daysBetween(baby.dueDate!, date);
    if (corrected < 0) return { reason: "before-due-date" };
    // Below the chronological age, so within the table; said, not assumed.
    if (corrected > GROWTH_MAX_DAYS) return { reason: "past-two" };
    return { sex: baby.sex, days: corrected, corrected: true };
  }
  return { sex: baby.sex, days: age, corrected: false };
}

function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  const suffix = { 1: "st", 2: "nd", 3: "rd" }[n % 10] ?? "th";
  return `${n}${suffix}`;
}

/**
 * A percentile (0–100) as a parent hears it at the clinic: "38th percentile".
 * The ends read "under the 1st percentile" and "over the 99th percentile".
 * The words describe, never judge: no "low", "high", or "behind".
 */
export function percentileText(percentile: number): string {
  const rounded = Math.round(percentile);
  if (rounded < 1) return "under the 1st percentile";
  if (rounded > 99) return "over the 99th percentile";
  return `${ordinal(rounded)} percentile`;
}
